import styled from 'styled-components';
import { Icon } from 'components/common';
import { colours } from 'utils/theme';

const BubbleTextCSS = styled.div.withConfig({
  shouldForwardProp: prop => prop !== '$suggestion',
})`
  color: ${colours.character.primary85};
  word-break: ${p => (p.$suggestion ? 'normal' : 'break-word')};
  font-size: 12px;
  line-height: 14px;
  text-align: left;
  ${p =>
    p.$suggestion &&
    `
    min-width: 0;
    display: flex;
  `}
`;

const Bubble = styled(BubbleTextCSS).withConfig({
  shouldForwardProp: prop =>
    prop !== 'isUser' && prop !== 'suggestion' && prop !== '$suggestion',
})`
  align-self: ${p =>
    p.suggestion ? 'flex-start' : p.isUser ? 'flex-end' : 'flex-start'};
  background-color: ${p => (p.suggestion || p.isUser ? '#fff' : '#FFCF9F')};
  border-radius: 20px;
  border: ${p =>
    p.suggestion || p.isUser
      ? `1px solid ${colours.borderGreyLight}`
      : undefined};
  padding: 6px 12px;
`;

const BubbleButton = styled(Bubble).withConfig({
  shouldForwardProp: prop =>
    prop !== 'isUser' &&
    prop !== 'suggestion' &&
    prop !== '$suggestion' &&
    prop !== '$fullWidth',
})`
  cursor: pointer;
  ${p =>
    p.$suggestion &&
    `
    box-sizing: border-box;
    width: ${p.$fullWidth ? '100%' : 'fit-content'};
    max-width: 100%;
    min-width: 0;
  `}
  &:hover {
    border-color: ${colours.linkBlue};
    color: ${colours.linkBlue};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #f0f0f0;
  }
`;

const BubbleContent = styled.span.withConfig({
  shouldForwardProp: prop => prop !== '$suggestion',
})`
  display: ${p => (p.$suggestion ? 'flex' : 'inline-flex')};
  align-items: center;
  gap: 6px;
  ${p =>
    p.$suggestion &&
    `
    min-width: 0;
  `}
`;

const BubbleTextSpan = styled.span.withConfig({
  shouldForwardProp: prop => prop !== '$suggestion' && prop !== '$fullWidth',
})`
  ${p =>
    p.$suggestion &&
    `
    flex: 0 1 auto;
    min-width: 0;
    ${
      p.$fullWidth
        ? 'white-space: normal;'
        : 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
    }
  `}
`;

export const NLPConversationBubble = ({
  isUser = true,
  /** When true, left-align like assistant bubbles but keep neutral pill styling (modal suggestions). */
  suggestion = false,
  /** When true (with suggestion), stretch to the container width and wrap text instead of
   * fitting/truncating to content — used for the follow-up chip list. */
  fullWidth = false,
  children,
  onClick,
  disabled = false,
  icon,
  testId,
}) => {
  const titleText =
    suggestion && typeof children === 'string' ? children : undefined;

  const content = (
    <BubbleContent $suggestion={suggestion}>
      {icon ? (
        <span
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Icon icon={icon} size='xs' color={colours.character.primary85} />
        </span>
      ) : null}
      <BubbleTextSpan $suggestion={suggestion} $fullWidth={fullWidth}>
        {children}
      </BubbleTextSpan>
    </BubbleContent>
  );

  if (onClick) {
    return (
      <BubbleButton
        as='button'
        type='button'
        isUser={isUser}
        suggestion={suggestion}
        $suggestion={suggestion}
        $fullWidth={fullWidth}
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        title={titleText}
      >
        {content}
      </BubbleButton>
    );
  }
  return (
    <Bubble style={{ maxWidth: '80%' }} isUser={isUser}>
      {content}
    </Bubble>
  );
};
